import { ref } from "vue";
import EmailValidator from "email-validator";
import dayjs from "dayjs";

import { login, getCodeApi, getIpHomeAddress } from "~/api/user";
import { createSocialLoginClient } from "~/composables/social-login-client";
import { thirdLoginList } from "~/hooks/login/thirdLoginConfig";
import { useLoading } from "~/hooks/useLoading";
import usePixel from "~/hooks/usePixel";
import { useMsgCountStore } from "~/stores/msg-count";
import { useUserStore } from "~/stores/user";
import { isSandbox } from "~/utils/common";
import { customTrackEvent } from "~/utils/customTrackEvent";
import eventBus, { EVENT_TYPES } from "~/utils/eventBus";
import showMessage from "~/utils/showMessage";

const inviteParams = ref(null);

const getOriginalRedirectUri = () => {
	if (!import.meta.client) return "";
	const currentPath = window.location.pathname;
	if (currentPath === "/seer/login") return `${window.location.origin}/seer/login`;
	return window.location.href;
};

const SOCIAL_CALLBACK_QUERY_KEYS = [
	"code",
	"login_type",
	"type",
	"state",
	"scope",
	"authuser",
	"prompt",
	"fromLogin",
];

const clearThirdPartyLoginUrlParams = () => {
	if (!import.meta.client) return;

	const url = new URL(window.location.href);
	SOCIAL_CALLBACK_QUERY_KEYS.forEach(param => url.searchParams.delete(param));
	url.hash = "";

	// 同步清理 Vue Router query，避免仅 replaceState 后 route.query 仍残留 code，
	// 导致登录弹窗再次挂载时重复消费授权码。
	const query = {};
	url.searchParams.forEach((value, key) => {
		query[key] = value;
	});
	void navigateTo({ path: url.pathname, query, hash: url.hash || undefined }, { replace: true });
};

const useLogin = ({ state = dayjs().unix() + "testw", invite_params = null } = {}) => {
	const { startLoading, stopLoading } = useLoading();
	const { reportGooglePixelEvent } = usePixel();
	const useUser = useUserStore();
	const msgCountStore = useMsgCountStore();
	const { getUnreadMsgCountFn } = msgCountStore;

	const countdown = ref(0);
	const timer = ref(null);

	if (invite_params) {
		inviteParams.value = invite_params;
	}

	function setCountdown() {
		countdown.value = 60;
		timer.value = setInterval(() => {
			if (countdown.value <= 0) {
				clearInterval(timer.value);
				return;
			}
			countdown.value--;
		}, 1000);
	}

	async function finishLoginSuccess(data, callback = null, loginParams = {}) {
		const { token } = data || {};
		if (!token) return false;

		localStorage.setItem("token", token);
		if (loginParams.email) localStorage.setItem("gw_token_email", loginParams.email);
		if (loginParams.mobile) localStorage.setItem("gw_token_phone", loginParams.mobile);

		useUser.setThirdBindEmail(false);
		await useUser.refreshLoginStatus();
		getUnreadMsgCountFn();
		stopLoading();

		if (typeof callback === "function") {
			await callback(data);
		}

		reportGooglePixelEvent("gw_login");
		customTrackEvent("gw_login");

		if (loginParams.third_access_token) {
			clearThirdPartyLoginUrlParams();
		}

		return true;
	}

	function validEmail(email) {
		if (!email) {
			showMessage("請輸入郵箱");
			return false;
		}

		const bool = EmailValidator.validate(email);
		if (!bool) {
			showMessage("請輸入正確的郵箱");
		}

		return bool;
	}

	function validPhone(phone) {
		if (!phone) {
			showMessage("請輸入手機號");
			return false;
		}

		const bool = /^\d+$/.test(phone);
		if (!bool) {
			showMessage("請輸入數字");
		}

		return bool;
	}

	function validCode(code) {
		if (!code) {
			showMessage("請輸入驗證碼");
			return false;
		}

		const bool = /^\d{6}$/.test(code);
		if (!bool) {
			showMessage("驗證碼只能輸入 6 位數字");
		}

		return bool;
	}

	async function getCode(codeParams) {
		startLoading();
		try {
			const { data } = await getCodeApi(codeParams);
			const tip = data?.tips || "驗證碼發送成功";
			stopLoading();
			showMessage(tip, "success");
			setCountdown();
			return { success: true, cooldown_seconds: 60 };
		} catch (e) {
			console.error(e);
			setTimeout(() => {
				stopLoading();
			}, 1500);
			return { success: false, error_code: e?.code ?? "unknown" };
		}
	}

	async function getAreaByIpCode(areaCodes) {
		const defaultData = {
			code: "0086",
			text: "中國大陸",
		};

		try {
			const { data } = await getIpHomeAddress();
			const areaCode = data.area;
			const matchedCode = areaCode
				? areaCodes.find(area => `00${areaCode}`.indexOf(area.code) !== -1)
				: null;

			return matchedCode || defaultData;
		} catch (error) {
			console.error("默認區號匹配失敗:", error);
			return defaultData;
		}
	}

	async function handleLogin(loginParams, callback = null) {
		startLoading();

		const processLoginParams = {
			...loginParams,
			invite_params: inviteParams.value || null,
		};

		try {
			const res = await login(processLoginParams);
			await finishLoginSuccess(res.data, callback, loginParams);
			return {
				success: true,
				is_new_user: res.data?.is_new_user,
			};
		} catch (err) {
			if (err.code === 1075) {
				useUser.setThirdBindEmail(true);
				eventBus.emit(EVENT_TYPES.THIRD_PARTY_NEEDS_BIND_EMAIL);
			}
			setTimeout(() => {
				stopLoading();
			}, 1500);
			return { success: false, error_code: err?.code ?? "unknown" };
		}
	}

	function getSocialLoginTypeFromUrl(fallbackType) {
		if (!import.meta.client) return fallbackType;
		const params = new URLSearchParams(window.location.search);
		return params.get("login_type") || params.get("type") || fallbackType;
	}

	const socialLoginClient = createSocialLoginClient({
		isSandbox,
		lang: "zh_tw",
		platform: 8,
		openId: "占位",
		autoPersistToken: false,
		autoClearUrl: false,
		providers: thirdLoginList,
		callbackQuery: () => ({
			state: btoa(JSON.stringify({ ...state, redirect_uri: getOriginalRedirectUri() })),
		}),
		loginParams: () => ({
			invite_params: inviteParams.value || null,
		}),
		onNotify({ type, message }) {
			if (type === "error") showMessage(message, "error");
		},
		onLoadingChange(visible) {
			visible ? startLoading() : stopLoading();
		},
		onNeedBindEmail(bindContext) {
			useUser.setThirdBindEmailParams({
				login_type: bindContext.login_type,
				third_access_token: bindContext.third_access_token,
				open_id: bindContext.open_id || null,
			});
			useUser.setThirdBindEmail(true);
			eventBus.emit(EVENT_TYPES.THIRD_PARTY_NEEDS_BIND_EMAIL, bindContext);
		},
	});

	async function loginBySocialAuth(loginType) {
		if (!import.meta.client) return;
		startLoading();
		sessionStorage.setItem("third_party_login_redirect", "1");
		// OAuth 会把第三方页压进历史栈，返回时需跳过（见 safeNavigateBack）
		sessionStorage.setItem("oauth_history_dirty", "1");
		const isRedirecting = await socialLoginClient.redirectToProvider(loginType);
		if (!isRedirecting) {
			sessionStorage.removeItem("third_party_login_redirect");
			sessionStorage.removeItem("oauth_history_dirty");
			stopLoading();
		}
	}

	async function afterSocialAuthLogin(code, callback, extraLoginParams = {}, fallbackLoginType) {
		if (import.meta.client) sessionStorage.removeItem("third_party_login_redirect");
		const loginType = getSocialLoginTypeFromUrl(fallbackLoginType);
		try {
			const result = await socialLoginClient.handleCallback({ code, loginType }, extraLoginParams);

			if (result.status === "success") {
				await finishLoginSuccess(result.data, callback);
			}

			return result;
		} finally {
			clearThirdPartyLoginUrlParams();
		}
	}

	const loginByFacebook = () => loginBySocialAuth("facebook");
	const loginByLineAuth = () => loginBySocialAuth("line");
	const afterLineAuthLogin = (code, callback, extraLoginParams = {}) =>
		afterSocialAuthLogin(code, callback, extraLoginParams, "line");
	const loginByGoogleAuth = () => loginBySocialAuth("google");
	const afterGoogleAuthLogin = (code, callback, extraLoginParams = {}) =>
		afterSocialAuthLogin(code, callback, extraLoginParams, getSocialLoginTypeFromUrl("google"));
	const loginByApple = () => {
		showMessage("Apple 登入暫未開放", "error");
	};

	return {
		thirdLoginList,
		countdown,
		validEmail,
		validCode,
		validPhone,
		getAreaByIpCode,
		getCode,
		handleLogin,
		loginByFacebook,
		loginByLineAuth,
		afterLineAuthLogin,
		loginByGoogleAuth,
		afterGoogleAuthLogin,
		loginByApple,
	};
};

export default useLogin;
